"use strict";

exports.__esModule = true;
exports.MeasurementValue = void 0;
exports.resolveLabelHeight = resolveLabelHeight;
var _react = _interopRequireWildcard(require("react"));
var _polished = require("polished");
var _styledComponents = require("styled-components");
var _UnitValue = require("../UnitValue");
var _TextValue = require("../TextValue");
var _approximateFontSize = require("../approximateFontSize");
var _guessRelativeTextSize = require("../guessRelativeTextSize");
var _fitLabel = require("../fitLabel");
var _calculateXY2 = require("../calculateXY");
var _constants = require("../../constants");
var _TileContext = require("../../Widgets/common/TileContext");
var _Catalogs = require("../../../Catalogs");
var _UnitValue2 = require("../UnitValue/UnitValue");
var _Utils = require("../../../Utils");
var _unitDefinitions = require("../../../Catalogs/Units/unitDefinitions");
var _excluded = ["label", "labelSize", "width", "height", "units", "textAnchor", "value", "fontSize", "color", "backgroundColor", "hideMtcAgentUnits", "centerVertically", "dataTestId"];
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
// The value as UnitValue draws it: durations as "3h 5m", a fraction such as
// 0.6012345678 as "60" with its symbol ("%", not the unit key "percent").
function formatForUnits(value, units) {
  if (value == null || value === "") {
    return {
      text: "",
      symbol: ""
    };
  }
  if (units === _unitDefinitions.UNIT_MILLISECOND || units === _unitDefinitions.UNIT_SECOND) {
    var number = Number(value) || 0;
    return {
      text: (0, _Utils.formatDuration)(units === _unitDefinitions.UNIT_MILLISECOND ? number : number * 1000),
      symbol: ""
    };
  }
  var definition = units ? (0, _Catalogs.getUnitDefinition)(units) : undefined;
  if (!definition) {
    return {
      text: String(value),
      symbol: ""
    };
  }
  return {
    text: (0, _UnitValue2.formatUnitValue)(value, definition),
    symbol: definition.symbol || ""
  };
}

// The text to size the value by. Estimating with the raw value or the unit
// key made it look many characters wide and shrank it to fit; UnitValue draws
// the symbol at half size and sizes itself by half of it.
function displayText(value, units) {
  var _formatForUnits = formatForUnits(value, units),
    text = _formatForUnits.text,
    symbol = _formatForUnits.symbol;
  return symbol ? "" + text + symbol.slice(Math.floor(symbol.length / 2)) : text;
}

// KPI tile from the next-gen design: a large bold value with a small uppercase
// label, inside a white card floating on a soft shadow. A state colour
// supplied by the widget becomes a tint of the card, not a solid fill.
//
// Tall tiles stack the value over the label. Short tiles put the label on the
// left and the value on the right so the tile still says what it shows. As a
// flat stat inside a device card the tile draws no card at all. Labels that
// would not fit are shortened with an ellipsis rather than clipped.
//
// `labelSize` (a fraction of the height, e.g. 0.2) reserves a label band of
// that size and always stacks; `centerVertically` centres a label-less value.
var TILE_PAD_X = 14;
var TILE_PAD_Y = 12;
var TILE_RADIUS = 12;
var LABEL_FONT_SIZE = 10.5;
var LABEL_LINE_HEIGHT = 16;
var MIN_HEIGHT_FOR_LABEL = 36;
// Below this a stacked tile uses a tighter label line.
var ROOMY_HEIGHT_FOR_LABEL = 48;
var TIGHT_LABEL_FONT_SIZE = 9;
var TIGHT_LABEL_LINE_HEIGHT = 12;
var MIN_HEIGHT_FOR_COMPACT = 24;
var MIN_WIDTH_FOR_COMPACT = 90;
var COMPACT_LABEL_FONT_SIZE = 9.5;
var COMPACT_LABEL_MIN_SHARE = 0.35;
var COMPACT_LABEL_MAX_SHARE = 0.72;
var COMPACT_GAP = 10;
var FLAT_LABEL_HEIGHT = 12;
var FLAT_LABEL_FONT_SIZE = 9.5;
var FLAT_VALUE_MAX_FONT_SIZE = 22;
var FLAT_ROOMY_HEIGHT = 60;
var FLAT_ROOMY_MAX_FONT_SIZE = 48;
// A roomy tile's label grows with it, from the card label size up to this.
var ROOMY_LABEL_MAX_FONT_SIZE = 13;
var ROOMY_LABEL_SHARE = 0.16;
var ROOMY_LABEL_LINE = 1.5;
var FALLBACK = {
  card: "#ffffff",
  shadow: "0 1px 2px rgba(29, 36, 41, 0.06), 0 6px 16px rgba(29, 36, 41, 0.08), 0 20px 40px -16px rgba(29, 36, 41, 0.14)",
  label: "#7C8A94",
  value: "#1B2227",
  heading: 'Archivo, "IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, Menlo, Consolas, "DejaVu Sans Mono", monospace'
};
var safeMix = function safeMix(amount, color, base) {
  try {
    return (0, _polished.mix)(amount, color, base);
  } catch (e) {
    return color;
  }
};

/**
 * Resolve vertical space reserved for the label when labelSize is provided.
 * @param {number} height widget height
 * @param {number} labelSize fraction of height (e.g. 0.2 = 20%)
 */
function resolveLabelHeight(height, labelSize) {
  return Math.min(height * 0.5, Math.max(12, height * labelSize));
}

/**
 * Displays a measurement value as a KPI tile.
 */
var MeasurementValue = exports.MeasurementValue = function MeasurementValue(props) {
  var label = props.label,
    labelSize = props.labelSize,
    width = props.width,
    height = props.height,
    units = props.units,
    _props$textAnchor = props.textAnchor,
    textAnchor = _props$textAnchor === void 0 ? _constants.TEXT_ANCHOR_MIDDLE : _props$textAnchor,
    value = props.value,
    fontSize = props.fontSize,
    color = props.color,
    backgroundColor = props.backgroundColor,
    hideMtcAgentUnits = props.hideMtcAgentUnits,
    centerVertically = props.centerVertically,
    _props$dataTestId = props.dataTestId,
    dataTestId = _props$dataTestId === void 0 ? "MeasurementValue" : _props$dataTestId,
    extra = _objectWithoutPropertiesLoose(props, _excluded);
  var theme = (0, _react.useContext)(_styledComponents.ThemeContext) || {};
  var tile = (0, _react.useContext)(_TileContext.TileContext) || {};
  var labelColor = theme.KpiLabelColor || FALLBACK.label;
  var valueColor = color || theme.KpiValueColor || FALLBACK.value;
  var headingFont = theme.FontFamilyHeading || FALLBACK.heading;
  var hasLabel = typeof label === "string" && label.length > 0;
  var labelText = hasLabel ? label.toUpperCase() : undefined;
  var hasLabelSize = labelSize != null && labelSize > 0;
  var renderValue = function renderValue(_ref) {
    var w = _ref.width,
      h = _ref.height,
      anchor = _ref.anchor,
      size = _ref.size,
      font = _ref.font,
      weight = _ref.weight;
    return !units ? /*#__PURE__*/_react["default"].createElement(_TextValue.TextValue, {
      width: w,
      height: h,
      value: value == null ? "" : String(value),
      textAnchor: anchor,
      fontSize: size,
      fontWeight: weight,
      fontFamily: font,
      color: valueColor,
      centerVertically: centerVertically
    }) : /*#__PURE__*/_react["default"].createElement(_UnitValue.UnitValue, {
      width: w,
      height: h,
      value: value,
      units: units,
      textAnchor: anchor,
      fontSize: size,
      color: valueColor,
      style: {
        fontFamily: font
      },
      hideMtcAgentUnits: hideMtcAgentUnits
    });
  };
  var renderLabel = function renderLabel(_ref2) {
    var text = _ref2.text,
      x = _ref2.x,
      y = _ref2.y,
      size = _ref2.size,
      anchor = _ref2.anchor;
    return /*#__PURE__*/_react["default"].createElement("text", {
      x: x,
      y: y,
      fontSize: size,
      fontWeight: 700,
      letterSpacing: "0.07em",
      fill: labelColor,
      textAnchor: anchor,
      alignmentBaseline: "middle",
      dominantBaseline: "middle",
      "data-testid": dataTestId + ".Label"
    }, text);
  };

  // A line inside a device card, with no card of its own. In a stat row it is
  // a small mono value over a tiny label; given a roomy cell it grows to fill.
  if (tile.flat) {
    var roomy = tile.prominent === true || height > FLAT_ROOMY_HEIGHT;
    var monoFont = theme.ChartFontFamilyMono || FALLBACK.mono;
    var flatFont = roomy ? headingFont : monoFont;
    var labelFontSize = roomy ? Math.min(ROOMY_LABEL_MAX_FONT_SIZE, Math.max(LABEL_FONT_SIZE, Math.round(height * ROOMY_LABEL_SHARE))) : FLAT_LABEL_FONT_SIZE;
    var labelHeight = hasLabel ? roomy ? Math.max(LABEL_LINE_HEIGHT, Math.round(labelFontSize * ROOMY_LABEL_LINE)) : FLAT_LABEL_HEIGHT : 0;
    var valueHeight = Math.max(0, height - labelHeight);
    var autoSize = (0, _approximateFontSize.approximateFontSize)({
      width: width,
      height: valueHeight,
      value: displayText(value, units)
    });
    var size = roomy ? Math.min(FLAT_ROOMY_MAX_FONT_SIZE, fontSize ? Math.min(fontSize, autoSize) : autoSize) : Math.min(FLAT_VALUE_MAX_FONT_SIZE, fontSize || autoSize);
    return /*#__PURE__*/_react["default"].createElement("svg", _extends({
      width: width,
      height: height
    }, extra, {
      "data-testid": dataTestId,
      style: Object.assign({
        display: "block",
        overflow: "visible"
      }, extra.style)
    }), /*#__PURE__*/_react["default"].createElement("svg", {
      x: 0,
      y: 0,
      width: width,
      height: valueHeight,
      style: {
        overflow: "visible"
      }
    }, renderValue({
      width: width,
      height: valueHeight,
      anchor: "start",
      size: size,
      font: flatFont,
      weight: 700
    })), hasLabel && renderLabel({
      text: (0, _fitLabel.fitLabel)(labelText, labelFontSize, width),
      x: 0,
      y: height - labelHeight / 2,
      size: labelFontSize,
      anchor: "start"
    }));
  }
  var fill = backgroundColor ? safeMix(0.16, backgroundColor, "#ffffff") : theme.ChartCardBackground || FALLBACK.card;
  var shadow = theme.CardShadow || FALLBACK.shadow;
  var padX = width >= 120 ? TILE_PAD_X : Math.max(4, Math.floor(width / 10));
  var padY = height >= 80 ? TILE_PAD_Y : Math.max(3, Math.floor(height / 10));
  var innerWidth = Math.max(0, width - 2 * padX);

  // A reserved label band always stacks; otherwise stack when tall enough.
  var stacked = hasLabel && (hasLabelSize || height >= MIN_HEIGHT_FOR_LABEL);
  var compact = hasLabel && !stacked && height >= MIN_HEIGHT_FOR_COMPACT && width >= MIN_WIDTH_FOR_COMPACT;
  var body;
  if (compact) {
    // The value takes what it needs; the label gets the rest, so short
    // values such as "0s" leave room for a whole label.
    var _valueHeight = Math.max(0, height - 2 * padY);
    var valueText = displayText(value, units);
    var valueSize = (0, _approximateFontSize.fitFontSize)(fontSize, (0, _approximateFontSize.approximateFontSize)({
      width: innerWidth * (1 - COMPACT_LABEL_MIN_SHARE),
      height: _valueHeight,
      value: valueText
    }));
    var valueEstimate = Math.ceil((0, _guessRelativeTextSize.guessRelativeTextSize)(valueText) * valueSize * 0.62);
    var labelMaxWidth = Math.max(innerWidth * COMPACT_LABEL_MIN_SHARE, Math.min(innerWidth * COMPACT_LABEL_MAX_SHARE, innerWidth - valueEstimate - COMPACT_GAP));
    var _labelFontSize = Math.max(8, Math.min(COMPACT_LABEL_FONT_SIZE, (0, _approximateFontSize.approximateFontSize)({
      width: labelMaxWidth,
      height: 20,
      value: labelText
    })));
    var shownLabel = (0, _fitLabel.fitLabel)(labelText, _labelFontSize, labelMaxWidth);
    var valueX = padX + (0, _fitLabel.estimateLabelWidth)(shownLabel, _labelFontSize) + COMPACT_GAP;
    var valueWidth = Math.max(0, width - padX - valueX);
    body = /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, renderLabel({
      text: shownLabel,
      x: padX,
      y: height / 2,
      size: _labelFontSize,
      anchor: "start"
    }), /*#__PURE__*/_react["default"].createElement("svg", {
      x: valueX,
      y: padY,
      width: valueWidth,
      height: _valueHeight,
      style: {
        overflow: "visible"
      }
    }, renderValue({
      width: valueWidth,
      height: _valueHeight,
      anchor: "end",
      size: fontSize,
      font: headingFont,
      weight: 700
    })));
  } else {
    var _labelHeight = 0;
    var _labelFontSize2 = 0;
    if (stacked) {
      if (hasLabelSize) {
        // The widget asked for a band of this size; the label fills it.
        _labelHeight = resolveLabelHeight(height, labelSize);
        _labelFontSize2 = (0, _approximateFontSize.approximateFontSize)({
          width: innerWidth,
          height: _labelHeight,
          value: label
        });
      } else {
        var _roomy = height >= ROOMY_HEIGHT_FOR_LABEL;
        _labelHeight = _roomy ? LABEL_LINE_HEIGHT : TIGHT_LABEL_LINE_HEIGHT;
        _labelFontSize2 = Math.min(_roomy ? LABEL_FONT_SIZE : TIGHT_LABEL_FONT_SIZE, (0, _approximateFontSize.approximateFontSize)({
          width: innerWidth,
          height: _labelHeight,
          value: label
        }));
      }
    }
    var _valueHeight2 = Math.max(0, height - 2 * padY - _labelHeight);
    var _calculateXY = (0, _calculateXY2.calculateXY)({
        width: innerWidth,
        height: height,
        textAnchor: textAnchor
      }),
      x = _calculateXY.x;
    body = /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, /*#__PURE__*/_react["default"].createElement("svg", {
      x: padX,
      y: padY,
      width: innerWidth,
      height: _valueHeight2,
      style: {
        overflow: "visible"
      }
    }, renderValue({
      width: innerWidth,
      height: _valueHeight2,
      anchor: textAnchor,
      size: fontSize,
      font: headingFont,
      weight: 700
    })), stacked && renderLabel({
      text: (0, _fitLabel.fitLabel)(labelText, _labelFontSize2, innerWidth),
      x: padX + x,
      y: height - padY - _labelHeight / 2,
      size: _labelFontSize2,
      anchor: textAnchor
    }));
  }
  return /*#__PURE__*/_react["default"].createElement("div", {
    "data-testid": dataTestId + ".Card",
    style: {
      display: "inline-block",
      verticalAlign: "top",
      boxSizing: "border-box",
      width: width,
      height: height,
      borderRadius: TILE_RADIUS,
      background: fill,
      boxShadow: shadow,
      transition: "background .4s ease"
    }
  }, /*#__PURE__*/_react["default"].createElement("svg", _extends({
    width: width,
    height: height
  }, extra, {
    "data-testid": dataTestId,
    style: Object.assign({
      display: "block"
    }, extra.style)
  }), body));
};